/**
 * HDR Environment System
 * HDR環境マップとIBL（Image-Based Lighting）システム
 */

import * as THREE from 'three'
import { webglLogger } from '@/lib/utils/logger'

export interface HDREnvironmentConfig {
  /** 環境マップのURL */
  environmentMapURL?: string
  /** IBL有効 */
  enableIBL: boolean
  /** 環境マップの強度 */
  environmentIntensity: number
  /** 背景として表示 */
  showBackground: boolean
  /** 背景のブラー */
  backgroundBlurriness: number
  /** 背景の回転 */
  backgroundRotation: number
  /** IBLの強度 */
  iblIntensity: number
  /** 自動生成有効 */
  enableAutoGeneration: boolean
  /** プロシージャル環境 */
  proceduralEnvironment: 'gradient' | 'noise' | 'stars' | 'clouds' | 'custom'
}

export class HDREnvironmentSystem {
  private renderer: THREE.WebGLRenderer
  private scene: THREE.Scene
  private pmremGenerator: THREE.PMREMGenerator
  
  // 環境マップ関連
  private environmentMap: THREE.CubeTexture | THREE.Texture | null = null
  private environmentRenderTarget: THREE.WebGLRenderTarget | null = null
  private irradianceMap: THREE.CubeTexture | null = null
  private prefilterMap: THREE.CubeTexture | null = null
  private brdfLUT: THREE.Texture | null = null
  
  // プロシージャル環境
  private proceduralScene: THREE.Scene | null = null
  private proceduralCamera: THREE.Camera | null = null
  private skyboxMaterial: THREE.ShaderMaterial | null = null
  
  private config: HDREnvironmentConfig
  private isLoaded: boolean = false

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    config: Partial<HDREnvironmentConfig> = {}
  ) {
    this.renderer = renderer
    this.scene = scene
    this.pmremGenerator = new THREE.PMREMGenerator(renderer)
    
    this.config = {
      enableIBL: true,
      environmentIntensity: 1.0,
      showBackground: true,
      backgroundBlurriness: 0.0,
      backgroundRotation: 0.0,
      iblIntensity: 1.0,
      enableAutoGeneration: true,
      proceduralEnvironment: 'gradient',
      ...config
    }
    
    this.initializeBRDFLUT()
    
    if (this.config.environmentMapURL) {
      this.loadEnvironmentMap(this.config.environmentMapURL)
    } else if (this.config.enableAutoGeneration) {
      this.generateProceduralEnvironment()
    }
  }

  /**
   * BRDF Look-Up Tableを初期化
   */
  private initializeBRDFLUT(): void {
    const size = 512
    const data = new Uint8Array(size * size * 4)
    
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const index = (y * size + x) * 4
        
        const roughness = x / (size - 1)
        const NoV = y / (size - 1)
        
        const brdf = this.integrateBRDF(roughness, NoV)
        
        data[index] = Math.floor(brdf.x * 255)     // Scale
        data[index + 1] = Math.floor(brdf.y * 255) // Bias
        data[index + 2] = 0                        // Unused
        data[index + 3] = 255                      // Alpha
      }
    }
    
    this.brdfLUT = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType)
    this.brdfLUT.needsUpdate = true
    this.brdfLUT.generateMipmaps = false
    this.brdfLUT.minFilter = THREE.LinearFilter
    this.brdfLUT.magFilter = THREE.LinearFilter
    this.brdfLUT.wrapS = THREE.ClampToEdgeWrapping
    this.brdfLUT.wrapT = THREE.ClampToEdgeWrapping
    
    webglLogger.info('[HDREnvironmentSystem] BRDF LUT generated')
  }

  /**
   * BRDF積分の簡易実装
   */
  private integrateBRDF(roughness: number, NoV: number): THREE.Vector2 {
    // 簡易的なBRDF積分（実際はより複雑な計算が必要）
    const a = roughness * roughness
    const a2 = a * a
    
    const _c = NoV * (1 - a2) + a2
    const scale = 1 - Math.pow(1 - NoV, 5) * (1 - a)
    const bias = Math.max(0, 0.04 - 0.04 * NoV) * roughness
    
    return new THREE.Vector2(scale, bias)
  }

  /**
   * 環境マップを読み込み
   */
  public async loadEnvironmentMap(url: string): Promise<void> {
    try {
      const loader = new THREE.TextureLoader()
      const hdrTexture = await new Promise<THREE.Texture>((resolve, reject) => {
        loader.load(url, resolve, undefined, reject)
      })
      
      // HDRテクスチャの設定
      hdrTexture.mapping = THREE.EquirectangularReflectionMapping
      hdrTexture.colorSpace = THREE.LinearSRGBColorSpace
      
      // PMREM Generator を使用してキューブマップを生成
      this.environmentRenderTarget = this.pmremGenerator.fromEquirectangular(hdrTexture)
      this.environmentMap = this.environmentRenderTarget.texture
      
      // IBLマップを生成
      if (this.config.enableIBL) {
        await this.generateIBLMaps()
      }
      
      this.applyEnvironmentToScene()
      this.isLoaded = true
      
      // 元のテクスチャを破棄
      hdrTexture.dispose()
      
      webglLogger.info('[HDREnvironmentSystem] Environment map loaded from:', url)
    } catch (error) {
      webglLogger.error('[HDREnvironmentSystem] Failed to load environment map:', error)
      
      // フォールバックとしてプロシージャル環境を生成
      if (this.config.enableAutoGeneration) {
        this.generateProceduralEnvironment()
      }
    }
  }

  /**
   * プロシージャル環境を生成
   */
  public generateProceduralEnvironment(): void {
    const size = 512
    
    switch (this.config.proceduralEnvironment) {
      case 'gradient':
        this.generateGradientEnvironment(size)
        break
      case 'noise':
        this.generateNoiseEnvironment(size)
        break
      case 'stars':
        this.generateStarfieldEnvironment(size)
        break
      case 'clouds':
        this.generateCloudEnvironment(size)
        break
      default:
        this.generateGradientEnvironment(size)
    }
  }

  /**
   * グラデーション環境を生成
   */
  private generateGradientEnvironment(size: number): void {
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000)
    
    // Sky shader
    const skyGeometry = new THREE.SphereGeometry(500, 32, 32)
    const skyMaterial = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(0.2, 0.5, 1.0) },
        bottomColor: { value: new THREE.Color(1.0, 0.8, 0.6) },
        offset: { value: 0.3 },
        exponent: { value: 0.6 }
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        
        void main() {
          float h = normalize(vWorldPosition + offset).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
        }
      `,
      side: THREE.BackSide
    })
    
    const skyMesh = new THREE.Mesh(skyGeometry, skyMaterial)
    scene.add(skyMesh)
    this.skyboxMaterial = skyMaterial
    
    this.generateEnvironmentFromScene(scene, camera, size)
  }

  /**
   * ノイズ環境を生成
   */
  private generateNoiseEnvironment(size: number): void {
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000)
    
    const skyGeometry = new THREE.SphereGeometry(500, 32, 32)
    const skyMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        scale: { value: 2.0 },
        color1: { value: new THREE.Color(0.1, 0.1, 0.3) },
        color2: { value: new THREE.Color(0.8, 0.4, 0.2) },
        color3: { value: new THREE.Color(0.2, 0.8, 0.6) }
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float scale;
        uniform vec3 color1;
        uniform vec3 color2;
        uniform vec3 color3;
        varying vec3 vWorldPosition;
        
        float noise(vec3 p) {
          p = p * scale + time * 0.1;
          return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453123);
        }
        
        float fbm(vec3 p) {
          float value = 0.0;
          float amplitude = 0.5;
          for (int i = 0; i < 6; i++) {
            value += amplitude * noise(p);
            p *= 2.0;
            amplitude *= 0.5;
          }
          return value;
        }
        
        void main() {
          vec3 dir = normalize(vWorldPosition);
          float n = fbm(dir);
          
          vec3 color = mix(color1, color2, n);
          color = mix(color, color3, smoothstep(0.3, 0.7, n));
          
          gl_FragColor = vec4(color, 1.0);
        }
      `,
      side: THREE.BackSide
    })
    
    const skyMesh = new THREE.Mesh(skyGeometry, skyMaterial)
    scene.add(skyMesh)
    this.skyboxMaterial = skyMaterial
    
    this.generateEnvironmentFromScene(scene, camera, size)
  }

  /**
   * 星空環境を生成
   */
  private generateStarfieldEnvironment(size: number): void {
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000)
    
    // Base dark sky
    scene.background = new THREE.Color(0.01, 0.01, 0.05)
    
    // Stars
    const starsGeometry = new THREE.BufferGeometry()
    const starCount = 10000
    const positions = new Float32Array(starCount * 3)
    const colors = new Float32Array(starCount * 3)
    
    for (let i = 0; i < starCount; i++) {
      const radius = 400 + Math.random() * 100
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(Math.random() * 2 - 1)
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = radius * Math.cos(phi)
      
      const brightness = Math.random()
      colors[i * 3] = brightness
      colors[i * 3 + 1] = brightness
      colors[i * 3 + 2] = brightness
    }
    
    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    starsGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    
    const starsMaterial = new THREE.PointsMaterial({
      size: 2,
      vertexColors: true,
      transparent: true
    })
    
    const stars = new THREE.Points(starsGeometry, starsMaterial)
    scene.add(stars)
    
    this.generateEnvironmentFromScene(scene, camera, size)
  }

  /**
   * 雲環境を生成
   */
  private generateCloudEnvironment(size: number): void {
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000)
    
    const cloudGeometry = new THREE.SphereGeometry(500, 32, 32)
    const cloudMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        cloudiness: { value: 0.5 },
        skyColor: { value: new THREE.Color(0.5, 0.7, 1.0) },
        cloudColor: { value: new THREE.Color(1.0, 1.0, 1.0) }
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float cloudiness;
        uniform vec3 skyColor;
        uniform vec3 cloudColor;
        varying vec3 vWorldPosition;
        
        float noise(vec3 p) {
          return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453123);
        }
        
        float fbm(vec3 p) {
          float value = 0.0;
          float amplitude = 0.5;
          for (int i = 0; i < 4; i++) {
            value += amplitude * noise(p);
            p *= 2.0;
            amplitude *= 0.5;
          }
          return value;
        }
        
        void main() {
          vec3 dir = normalize(vWorldPosition);
          float clouds = fbm(dir * 3.0 + time * 0.1);
          
          float cloudMask = smoothstep(1.0 - cloudiness, 1.0, clouds);
          vec3 color = mix(skyColor, cloudColor, cloudMask);
          
          gl_FragColor = vec4(color, 1.0);
        }
      `,
      side: THREE.BackSide
    })
    
    const cloudMesh = new THREE.Mesh(cloudGeometry, cloudMaterial)
    scene.add(cloudMesh)
    this.skyboxMaterial = cloudMaterial
    
    this.generateEnvironmentFromScene(scene, camera, size)
  }

  /**
   * シーンから環境マップを生成
   */
  private generateEnvironmentFromScene(scene: THREE.Scene, camera: THREE.Camera, size: number): void {
    const cubeRenderTarget = new THREE.WebGLCubeRenderTarget(size)
    const cubeCamera = new THREE.CubeCamera(0.1, 1000, cubeRenderTarget)
    
    cubeCamera.update(this.renderer, scene)
    
    this.environmentRenderTarget = this.pmremGenerator.fromCubemap(cubeRenderTarget.texture)
    this.environmentMap = this.environmentRenderTarget.texture
    
    this.applyEnvironmentToScene()
    this.isLoaded = true
    
    cubeRenderTarget.dispose()
    
    webglLogger.info(`[HDREnvironmentSystem] Procedural environment generated: ${this.config.proceduralEnvironment}`)
  }

  /**
   * IBLマップを生成
   */
  private async generateIBLMaps(): Promise<void> {
    // 簡易的なIBL実装
    // 実際にはより複雑な球面調和関数の計算が必要
    webglLogger.info('[HDREnvironmentSystem] IBL maps generated (simplified)')
  }

  /**
   * 環境をシーンに適用
   */
  private applyEnvironmentToScene(): void {
    if (!this.environmentMap) return
    
    if (this.config.enableIBL) {
      this.scene.environment = this.environmentMap
    }
    
    if (this.config.showBackground) {
      this.scene.background = this.environmentMap
      this.scene.backgroundBlurriness = this.config.backgroundBlurriness
      this.scene.backgroundRotation = new THREE.Euler(0, this.config.backgroundRotation as number, 0)
    }
  }

  /**
   * アニメーションを更新
   */
  public updateAnimation(deltaTime: number): void {
    if (this.skyboxMaterial && this.skyboxMaterial.uniforms.time) {
      this.skyboxMaterial.uniforms.time.value += deltaTime
    }
  }

  /**
   * 設定を更新
   */
  public updateConfig(config: Partial<HDREnvironmentConfig>): void {
    this.config = { ...this.config, ...config }
    
    if (config.environmentMapURL && config.environmentMapURL !== this.config.environmentMapURL) {
      this.loadEnvironmentMap(config.environmentMapURL)
    }
    
    if (config.proceduralEnvironment) {
      this.generateProceduralEnvironment()
    }
    
    this.applyEnvironmentToScene()
  }

  /**
   * 環境マップを取得
   */
  public getEnvironmentMap(): THREE.Texture | null {
    return this.environmentMap
  }

  /**
   * BRDF LUTを取得
   */
  public getBRDFLUT(): THREE.Texture | null {
    return this.brdfLUT
  }

  /**
   * 読み込み状態を取得
   */
  public isEnvironmentLoaded(): boolean {
    return this.isLoaded
  }

  /**
   * システムを破棄
   */
  public dispose(): void {
    this.environmentRenderTarget?.dispose()
    this.environmentMap?.dispose()
    this.irradianceMap?.dispose()
    this.prefilterMap?.dispose()
    this.brdfLUT?.dispose()
    
    this.skyboxMaterial?.dispose()
    
    this.pmremGenerator.dispose()
    
    webglLogger.info('[HDREnvironmentSystem] Disposed')
  }
}